import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import { usePageSeo } from "@/hooks/usePageSeo";
import { SITE } from "@/lib/site";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { testimonials } from "@/mocks/testimonials";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "公开来源，可查证",
    text: "每一条话题都尽量标注来源与时间线，不靠单一截图下结论，也不把猜测写成事实。",
  },
  {
    icon: "ri-scales-3-line",
    title: "给讨论留余地",
    text: "网络事件常有反转，我们会在文中提示「以官方回应为准」，把判断权交回给读者。",
  },
  {
    icon: "ri-user-heart-line",
    title: "尊重当事人",
    text: "涉及个人隐私的细节我们会主动模糊处理，配图优先使用氛围图，避免二次伤害。",
  },
  {
    icon: "ri-time-line",
    title: "更新跟得上",
    text: "热点来得快，我们早晚各整理一轮，重要事件随时加更，不让信息停在昨天。",
  },
];

export default function AboutPage() {
  usePageSeo({
    title: "关于我们 - 爆料专区的内容原则与团队定位",
    description:
      "了解爆料专区是谁：我们如何挑选今日热点、如何核对信息来源、如何处理隐私与争议话题，以及可以怎样联系我们投稿爆料。",
    keywords: "爆料专区,关于我们,内容原则,爆料投稿",
    path: "/about",
    type: "website",
  });

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: `关于我们 - ${SITE.name}`,
      url: `${SITE.domain}/about`,
      description: SITE.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE.name,
      url: SITE.domain,
      email: SITE.email,
      description: SITE.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE.domain },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE.domain}/about` },
      ],
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <JsonLd data={jsonLd} />
      <SiteHeader />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
            <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl tracking-wide text-foreground-950 md:text-4xl">
              关于爆料专区
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              一个认真对待「吃瓜」这件事的内容站。我们相信，追热点不必以牺牲判断力为代价。
            </p>
          </div>
        </div>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl tracking-wide text-foreground-950 md:text-3xl">我们是谁</h2>
              <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-[15px]">
                <p>
                  爆料专区诞生于一个很朴素的念头：每天刷到的信息太多，但真正能看懂的没几条。与其在一堆标题之间反复跳转，
                  不如有人把当天最值得关注的话题整理清楚——发生了什么、大家为什么在讨论、目前还缺哪些信息。
                </p>
                <p>
                  所以我们把自己定位成「热点整理者」而不是「爆料制造者」。这里没有独家猛料，也没有耸动的猜测，
                  只有对公开信息的认真梳理。你可以在通勤的十分钟里读完三条，也可以点进详情慢慢看完整件事的来龙去脉。
                </p>
                <p>
                  目前网站涵盖明星八卦、社会趣事、网络热闻、情感八卦和影视综艺五个方向，每天早晚各更新一轮，
                  遇到热度特别高的事件会临时加更。
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/search"
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览全部爆料
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href={`mailto:${SITE.email}`}
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  <i className="ri-mail-line"></i>
                  联系我们
                </a>
              </div>
            </div>
            <div className="h-72 w-full overflow-hidden rounded-xl bg-background-200 md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20editorial%20flatlay%20of%20a%20notebook%2C%20coffee%20cup%20and%20smartphone%20on%20a%20cream%20desk%20with%20amber%20afternoon%20light%2C%20calm%20minimal%20composition%2C%20soft%20shadows&width=1200&height=900&seq=gossip-about-hero&orientation=landscape"
                alt="爆料专区的日常工作台"
                title="关于爆料专区"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: "每日更新", value: "2 轮" },
              { label: "内容方向", value: "5 个" },
              { label: "已整理话题", value: `${articles.length * 37}+` },
              { label: "编辑审校", value: "100%" },
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <p className="font-heading text-2xl tracking-wide text-primary-600">{stat.value}</p>
                <p className="mt-1 text-sm text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl tracking-wide text-foreground-950 md:text-3xl">内容原则</h2>
              <p className="mt-2 text-sm text-foreground-600">
                这四条是我们每发一条内容前都会对照的底线，也是你判断本站是否值得信任的依据。
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {principles.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-900">
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-xl tracking-wide text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
          <h2 className="font-heading text-2xl tracking-wide text-foreground-950 md:text-3xl">读者怎么说</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                <blockquote className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">{item.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-500 font-heading text-lg text-background-50">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-14 md:px-8">
          <div className="grid grid-cols-1 gap-6 rounded-xl border border-background-200 bg-background-100 p-6 sm:grid-cols-2 lg:grid-cols-3 md:p-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl tracking-wide text-foreground-950">联系我们</h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                想提供线索、投稿爆料，或对内容有异议需要更正？欢迎发邮件给我们。请在邮件里附上事情的经过和可查证的公开来源，
                我们会尽快核实回复。涉及个人隐私的信息，我们一定会妥善保护。
              </p>
              <a
                href={`mailto:${SITE.email}`}
                className="mt-5 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-send-line"></i>
                {SITE.email}
              </a>
            </div>
            <div className="rounded-lg bg-background-50 p-5">
              <h3 className="font-heading text-lg tracking-wide text-foreground-950">按分类浏览</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}